import type { MessageKey } from '@lensmira/i18n'

import type { Translate } from './status-labels.js'

/**
 * A refusal, in the shop's own language where the catalogue has it. `LOC-001`.
 *
 * ---
 *
 * **Every refusal a shopkeeper reads today is in English, and no translator can reach any of
 * them.** `WriteRejected` carries a `code` and a `message`, and the message is written in
 * `apps/api`: "This sale was made against a prescription, which belongs to the customer it names."
 * `BlockedWrites` renders it as sent, the corrections panel renders its withheld rows as sent, and
 * both sit on a page that is otherwise Urdu. `LOC-001` says the catalogue is the only place a
 * translator can reach, so a sentence built on the server is a sentence nobody can translate.
 *
 * **The code is what makes it fixable.** Every refusal already carries one, and a code is a
 * stable identifier rather than prose, so the counter can look it up and fall back to the server's
 * words when it has none. That fallback is `ARC-020`'s "accept a value you have never heard of"
 * applied to copy: a refusal this build has no sentence for still says something, and says the
 * true thing, rather than being swallowed.
 *
 * **What is here and what is not.** The table holds the refusals whose sentence needs no
 * parameters, which is most of them. The ones that name a month, a job number or an amount are
 * deliberately absent: a catalogue sentence needs those as fields rather than baked into a string,
 * so the payload has to grow a `details` object first. `later_todo.md` 48 carries that, and until
 * then they fall back to the server's English, which is what they do now.
 *
 * **It is not a translation of the catalogue into a second place.** The server keeps writing the
 * sentence, because a refusal with no catalogue entry must still read as something, and because
 * `apps/api` is where the reason is known. This is the layer that prefers a translated one.
 */
const REFUSALS: Readonly<Record<string, MessageKey>> = {
  // The statement route's only parameterless refusal, and the one that matters most to
  // translate: `PanelStatements` pre-checks nothing, so its refusals do reach the counter.
  STATEMENT_DOES_NOT_RECONCILE: 'refusal.statementDoesNotReconcile',
  // §15.25's corrections, which is where this started: four of its five refusals need no
  // parameter and the fifth names a month.
  SALE_CARRIES_A_PRESCRIPTION: 'refusal.saleCarriesAPrescription',
  SALE_HAS_A_JOB: 'refusal.saleHasAJob',
  COMMISSION_PERIOD_CLOSED: 'refusal.commissionPeriodClosed',
  CASH_SESSION_COUNTED: 'refusal.cashSessionCounted',
  CORRECTION_NO_CHANGE: 'refusal.correctionNoChange',
  CORRECTION_REASON_REQUIRED: 'refusal.correctionReasonRequired',

  /**
   * And one the shopkeeper should never have read in the server's words at all.
   *
   * `requireKey` answers "Every write needs an Idempotency-Key header", which is addressed to
   * whoever wrote the client. This is the one entry where the catalogue is better than the wire
   * rather than merely translatable.
   */
  IDEMPOTENCY_KEY_REQUIRED: 'refusal.idempotencyKeyRequired',
  /*
   * The appointment refusals, which had none. `BlockedWrites` renders whatever this map does not
   * carry as the server's own English sentence, on a page that is otherwise Urdu, and §6.14's
   * routes landed with nine catalogue keys and no entry here. These are the codes a shopkeeper
   * can actually act on: the rest are shapes the screen refuses before enqueueing.
   */
  APPOINTMENT_CLOSED: 'refusal.appointmentClosed',
  APPOINTMENT_RECORD_FROZEN: 'refusal.appointmentClosed',
  APPOINTMENT_SCHEDULE_MISSING: 'refusal.appointmentScheduleMissing',
  APPOINTMENT_IN_THE_PAST: 'refusal.appointmentInThePast',
  APPOINTMENT_TYPE_ARCHIVED: 'refusal.appointmentTypeArchived',
  APPOINTMENT_RESOURCE_ARCHIVED: 'refusal.resourceArchived',
  RESOURCE_ARCHIVED: 'refusal.resourceArchived',
  RESOURCE_DOUBLE_BOOKED: 'refusal.resourceDoubleBooked',
  RESOURCE_NOT_VISITING: 'refusal.resourceNotVisiting',
  ALREADY_STARTED: 'refusal.appointmentAlreadyStarted',
  NOT_STARTED: 'refusal.appointmentNotStarted',

  /*
   * §6.13's claims vertical, which landed with three controllers and no entry here.
   *
   * Every throw site was read before any of these went in: all thirteen state the refusal
   * with nothing interpolated, so the catalogue keeps everything the server knew. The ones
   * that name an amount or a month are not here, for the reason the block below gives.
   *
   * **Most of these a shopkeeper will never read, and that is the point.** A refused write
   * sits `blocked` at the head of the queue and holds every sale behind it, so
   * `ClaimCapture` and `PanelAccounts` pre-check what they can from data they already hold.
   * These are what answers the race: two tills capturing the same sale, or a plan
   * deactivated between the read and the write.
   */
  CLAIM_EXISTS: 'refusal.claimExists',
  NOT_A_MEMBER: 'refusal.notAMember',
  NOT_ELIGIBLE: 'refusal.notEligible',
  PLAN_DEACTIVATED: 'refusal.planDeactivated',
  ORDER_HAS_NO_CUSTOMER: 'refusal.orderHasNoCustomer',
  /**
   * The one here a client can actually provoke, rather than race into.
   *
   * `POST /claims/:claimId/events` refuses `remit` outright: a panel's payment is recorded
   * against the panel, where it gets a `panel_payment` row and a journal, and never as an
   * event on one claim, which would move `claim.paid` with neither. So this is the sentence
   * that explains where to go instead, and it is worth a shopkeeper reading it in Urdu.
   */
  EVENT_NOT_DIRECT: 'refusal.eventNotDirect',
  PLAN_REQUIRED: 'refusal.planRequired',
  PAYMENT_ALREADY_REVERSED: 'refusal.paymentAlreadyReversed',
  REVERSAL_NOT_REVERSIBLE: 'refusal.reversalNotReversible',
  PAYMENT_NOT_THIS_PANEL: 'refusal.paymentNotThisPanel',
  PANEL_NOT_FOUND: 'refusal.panelNotFound',
  PAYMENT_NOT_FOUND: 'refusal.paymentNotFound',
  /*
   * Not a claims refusal, and it is here because checking one of the statement codes meant
   * checking all seven. `refusals.test.ts` grouped this one with the five that interpolate a
   * revision, a period or a branch count, and at its throw site it says 'No such statement.'
   * with nothing in it. Parameterless, so the rule above puts it in the catalogue.
   */
  STATEMENT_NOT_FOUND: 'refusal.statementNotFound',
  REASON_REQUIRED: 'refusal.reasonRequired',
}

/**
 * **Three codes are deliberately not in that table, and taking them out was the fix.**
 *
 * They were in it, and each one threw away what the server knew:
 *
 * - `PERMISSION_DENIED` says "A manager may not do that. Allowed: owner." Who to fetch is the
 *   useful half, and a generic line deletes it.
 * - `VERSION_CONFLICT` names the record: "this appointment", "this person", "this record".
 * - `BRANCH_NOT_PERMITTED` says which branch it means, and `corrections.service.ts` has two
 *   different ones ("this record belongs to", "you are moving this record to").
 *
 * All three keys are on the Urdu-absent list, so replacing them bought no translation today and
 * cost the specifics immediately. They belong with `PERIOD_CLOSED` in `later_todo.md` 48: a
 * catalogue sentence for them needs the role, the record or the branch as a field.
 */

/**
 * The shop's sentence for a refusal, or the server's.
 *
 * `fallback` is never empty in practice, because every `WriteRejected` carries a message, but it
 * is optional here: a write that was refused with nothing to say is still a refusal and still has
 * to render something.
 */
export function refusalSentence(
  t: Translate,
  code: string | undefined,
  fallback: string | undefined,
): string | undefined {
  const key = code === undefined ? undefined : REFUSALS[code]
  if (key !== undefined) return t(key)
  return fallback
}

/** Whether the catalogue has this one, for a test that wants to say which half it is checking. */
export const hasRefusalSentence = (code: string): boolean => REFUSALS[code] !== undefined
